import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import axios from "axios";
import BASE_URL from "../config";
import {
  CaciStyles, CaciDetailStyles, PageHeader, SectionHead, SectionNote, Seal,
  Prose, Skel,
  useReveal, useScrollProgress,
  stripHtml, truncate, langPath, localized,
} from "../components/CaciUI";

/* ═══════════════════════════════════════════════════════════════════════════
   CHAMBREDETAIL
   ───────────────────────────────────────────────────────────────────────────
   Same endpoint (/api/chambre_de_commerces/edit/:id). What the screenshot of
   the previous version showed, and what this rewrite addresses:

   LAYOUT
   · The page was a two-column grid: a 320px identity card on the left and the
     description on the right. Most chambers have no description, so the right
     column was empty and roughly 60% of the viewport was blank white space
     with a narrow card marooned at the left edge. The identity card is now a
     full-width profile band that lays its contact details out horizontally,
     so the page reads the same whether a description exists or not.
   · The description, when there is one, gets its own section underneath at a
     proper reading measure instead of being squeezed beside the card.
   · The map was dropped in the previous rewrite. The original page had one,
     and a chamber of commerce is a physical building people need to find, so
     it is back — and it also gives the lower half of a sparse page something
     to hold.

   ROSTER ORDER — the important one
   · The previous version sorted ascending, "so the president appears first".
     The screenshot proves the opposite: it produced 3rd Vice-President,
     2nd Vice-President, 1st Vice-President, President — the president LAST.
     The CMS stores `order` with the most senior member highest, which is why
     the original b.order - a.order was right. Sorting is descending again.

   READABILITY
   · Phone numbers arrived as "028129596" and rendered as one unbroken run of
     digits. They are grouped for display (028 12 95 96) while the tel: href
     keeps the raw digits.
   · The address arrived as "Palais Consulaire.06, Boulevard Amilcar
     Cabral.Place des Martyrs.Bp100.1er Novembre,Alger,Algérie,16003" and
     wrapped into an unreadable block. Separators are normalised so it reads
     as one properly punctuated line.
   · Roles were mixed case from the database ("DIRECTEUR GENERAL" next to
     "1er Vice-président de la CACI"). They render as one consistent small
     uppercase label now, so the inconsistency stops being visible.
   · Names that arrive in full caps are title-cased — see TIDY_ALLCAPS_NAMES.

   HIERARCHY
   · Every member card was identical, so you could not tell the president from
     a committee member. The first entry in each roster — the most senior,
     after the sort above — gets a wider card and a brass frame.

   Everything from the previous pass is kept: Member stays at module scope (a
   component declared inside the body is remounted on every scroll frame by
   useScrollProgress, which is what made both rosters invisible), the roster
   normaliser still accepts an array / keyed object / JSON string, and the
   not-found panel still renders in place instead of navigating during render.
   ═══════════════════════════════════════════════════════════════════════════ */

/* Set to false to print names exactly as the database holds them. */
const TIDY_ALLCAPS_NAMES = true;

/* Several of these fields arrive as arrays, and some as a bare string.
   Normalising once means the render never has to care which. */
function asList(value) {
  if (!value) return [];
  const arr = Array.isArray(value) ? value : [value];
  return arr.map((v) => String(v || "").trim()).filter(Boolean);
}

/* A roster does not always come back as a JSON array. Laravel serialises a
   collection whose keys are not 0..n — anything through keyBy(), or filtered
   or sorted without ->values() — as an OBJECT: {"12": {...}, "45": {...}}.
   Spreading that throws "object is not iterable", and .length on it is simply
   undefined, so the section vanishes with no error. Accept all three shapes.

   Sorted DESCENDING by `order`: the CMS numbers the most senior member
   highest. Ascending is what put the president at the end of the row. */
function asRoster(value) {
  let v = value;

  if (typeof v === "string") {
    const s = v.trim();
    if (!s) return [];
    try { v = JSON.parse(s); } catch (e) { return []; }
  }

  let list;
  if (Array.isArray(v)) list = v;
  else if (v && typeof v === "object") list = Object.values(v);
  else return [];

  /* Number(null) and Number("") are both 0, so a blank column has to be
     rejected before the numeric test rather than by it. Missing orders sink
     to the bottom of a descending sort, which is where they belong. */
  const rank = (m) => {
    const o = m.order;
    if (o === null || o === undefined || o === "") return -Infinity;
    const n = Number(o);
    return Number.isFinite(n) ? n : -Infinity;
  };

  return list
    .filter((m) => m && typeof m === "object")
    .sort((a, b) => rank(b) - rank(a));
}

/* "KOUIDRI CHAKIB ISMAIL" -> "Kouidri Chakib Ismail".
   Only touches strings that are entirely Latin uppercase, so "MADJENE
   Lakhdar" (deliberate surname caps) and any Arabic name are left alone. */
function tidyName(raw) {
  const s = String(raw || "").trim();
  if (!TIDY_ALLCAPS_NAMES || !s) return s;
  if (/[\u0600-\u06FF]/.test(s)) return s;   // Arabic: no case at all
  if (!/[A-ZÀ-Þ]/.test(s)) return s;         // nothing uppercase to fix
  if (/[a-zà-öø-ÿ]/.test(s)) return s;       // already mixed case
  return s
    .toLowerCase()
    .replace(/(^|[\s'’\-.])([a-zà-öø-ÿ])/g, (m, sep, ch) => sep + ch.toUpperCase());
}

/* Algerian numbering: 9 digits for a landline (028 12 95 96), 10 for a mobile
   (05 55 12 34 56). Anything else is returned untouched rather than guessed
   at, and the href always uses the raw digits. */
function formatPhone(raw) {
  const s = String(raw || "").trim();
  const d = s.replace(/[^\d+]/g, "");
  const local = d.replace(/^\+213/, "0").replace(/[^\d]/g, "");
  if (local.length === 9 && local.startsWith("0")) {
    return `${local.slice(0, 3)} ${local.slice(3, 5)} ${local.slice(5, 7)} ${local.slice(7, 9)}`;
  }
  if (local.length === 10 && local.startsWith("0")) {
    return `${local.slice(0, 2)} ${local.slice(2, 4)} ${local.slice(4, 6)} ${local.slice(6, 8)} ${local.slice(8, 10)}`;
  }
  return s;
}

const telHref = (raw) => String(raw || "").replace(/[^\d+]/g, "");

/* The CMS stores addresses with full stops used as commas and no spaces after
   them: "Palais Consulaire.06, Boulevard Amilcar Cabral.Place des Martyrs..."
   Normalising every separator to ", " fixes the wrapping, but it also splits
   a street number away from its street ("Consulaire, 06, Boulevard"), so a
   segment that is nothing but digits is folded into the one after it. */
function formatAddress(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";

  const parts = s
    .replace(/\s*[.,;؛،]\s*/g, "\u0001")
    .split("\u0001")
    .map((p) => p.trim())
    .filter(Boolean);

  const out = [];
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    // A bare 1–4 digit run is a street number, not its own line — unless it
    // is the last segment, where it is the postcode.
    if (/^\d{1,4}$/.test(p) && i < parts.length - 1) {
      parts[i + 1] = `${p} ${parts[i + 1]}`;
      continue;
    }
    if (out.length && out[out.length - 1].toLowerCase() === p.toLowerCase()) continue;
    out.push(p);
  }
  return out.join(", ");
}

const num = (v) => {
  const n = Number(String(v ?? "").trim());
  return Number.isFinite(n) && n !== 0 ? n : null;
};

/* ── One contact row ───────────────────────────────────────────────────────── */
const ContactRow = ({ icon, label, children, wide }) => (
  <div className={`cd-contact-row${wide ? " is-wide" : ""}`}>
    <span className="cd-contact-icon" aria-hidden="true"><i className={`bi bi-${icon}`} /></span>
    <span className="cd-contact-body">
      <span className="cd-contact-label">{label}</span>
      <span className="cd-contact-value">{children}</span>
    </span>
  </div>
);

/* ── Member card ─────────────────────────────────────────────────────────────
   MUST STAY AT MODULE SCOPE. Declared inside ChambreDetail it is a new
   function reference every render, so React unmounts and remounts each card;
   .member carries data-rv="rise" (opacity:0 until the reveal observer adds
   .rv-in to that exact node) and useScrollProgress() re-renders the page on
   every scroll frame — so scrolling to the rosters recreated the cards
   frame by frame and they could never become visible. */
const Member = ({ m, i, lang, lead }) => {
  const name = tidyName(stripHtml(localized(m, "name", lang)));
  const role = stripHtml(localized(m, "fonction", lang));
  const img = m.image ? `${BASE_URL}/${String(m.image).replace(/^\//, "")}` : null;

  return (
    <article
      className={`member${lead ? " is-lead" : ""}`}
      data-rv="rise"
      style={{ "--i": i % 6 }}
    >
      <div className="member-photo">
        {img && (
          <img
            src={img} alt={name || ""} loading="lazy" decoding="async"
            onError={(e) => {
              e.currentTarget.style.display = "none";
              e.currentTarget.parentElement.classList.add("is-empty");
            }}
          />
        )}
        <span className="member-fallback" aria-hidden="true"><i className="bi bi-person" /></span>
        {lead && <span className="member-pin" aria-hidden="true"><Seal size={16} /></span>}
      </div>
      <div className="member-info">
        <h3 dir="auto">{name}</h3>
        {role && <p dir="auto">{role}</p>}
      </div>
    </article>
  );
};

/* ── Roster block ──────────────────────────────────────────────────────────── */
const Roster = ({ title, members, lang }) => (
  <section className="cd-team">
    <SectionHead title={title} />
    <div className="member-grid">
      {members.map((m, i) => (
        <Member key={m.id ?? `${title}-${i}`} m={m} i={i} lang={lang} lead={i === 0} />
      ))}
    </div>
  </section>
);

function ChambreDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;
  const isRtl = lang === "ar";

  const [chambre, setChambre] = useState(null);
  const [state, setState] = useState("loading");

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const load = useCallback(() => {
    setState("loading");
    return axios
      .get(`${BASE_URL}/api/chambre_de_commerces/edit/${id}`)
      .then((res) => {
        if (!alive.current) return;
        /* Was `!res.data.id`, which declared a perfectly good chamber missing
           whenever the record came back under a different primary key or
           wrapped in { data: ... }. */
        const body = res.data && res.data.data ? res.data.data : res.data;
        const found =
          body && typeof body === "object" &&
          (body.id ?? body.ID ?? body.id_chambre ?? body.name ?? body.name_ar ?? body.name_en) != null;
        if (!found) { setState("missing"); return; }
        setChambre(body);
        setState("ready");
      })
      .catch((err) => {
        if (!alive.current) return;
        console.error(`ChambreDetail: chamber ${id} failed to load —`, err);
        setState(err?.response?.status === 404 ? "missing" : "error");
      });
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "auto" }); }, [id]);

  const pageRef = useReveal([chambre, state]);
  const progress = useScrollProgress();

  const name = chambre ? stripHtml(localized(chambre, "name", lang)) : "";
  const bodyHtml = chambre ? localized(chambre, "description", lang) : "";
  const summary = truncate(stripHtml(bodyHtml), 180);
  const logo = chambre?.logo ? `${BASE_URL}/assets/entreprise/${chambre.logo}` : null;
  const wilaya = String(chambre?.wilayas_name || "").trim();

  const emails = asList(chambre?.email);
  const phones = asList(chambre?.telfix);
  const faxes = asList(chambre?.fax);
  const address = formatAddress(chambre?.adresse);

  const lat = num(chambre?.latitude);
  const lng = num(chambre?.longitude);
  const hasMap = lat !== null && lng !== null;

  const admin = asRoster(chambre?.memberchamber_administratif);
  const elected = asRoster(chambre?.memberchamber_elu);

  /* If the chamber loads but both rosters are empty, that is the API's answer,
     not a rendering fault — say so once so nobody goes hunting through CSS. */
  useEffect(() => {
    if (state !== "ready" || !chambre) return;
    if (admin.length || elected.length) return;
    console.warn(
      `ChambreDetail: chamber ${id} returned no roster. ` +
      "memberchamber_administratif =", chambre.memberchamber_administratif,
      " memberchamber_elu =", chambre.memberchamber_elu,
      "— if both are undefined the /api/chambre_de_commerces/edit/:id endpoint " +
      "is not eager-loading those two relations."
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, chambre, id]);

  const listLabel = t("menu.chambres", "Chambres de commerce");

  /* Coordinates only. The address fallback sent people to whatever Google
     guessed from a free-text string — and these CMS addresses ("Palais
     Consulaire.06, Boulevard Amilcar Cabral...") routinely resolve to the
     wrong place, or to nothing. A wrong itinerary is worse than none, so
     without latitude AND longitude `directions` stays null and the button
     below never renders. */
  const directions = hasMap
    ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
    : null;

  const hasContact = emails.length + phones.length + faxes.length > 0 || !!address;

  /* ── Not found / failed ─────────────────────────────────────────────────── */
  if (state === "missing" || state === "error") {
    const missing = state === "missing";
    return (
      <div className={`caci-page${isRtl ? " is-rtl" : ""}`} dir={isRtl ? "rtl" : "ltr"}>
        <CaciStyles />
        <Helmet>
          <title>
            {missing ? t("notfound.title", "Introuvable") : t("search.errorTitle", "Erreur")} — {t("menu.caci")}
          </title>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <PageHeader
          title={missing ? t("notfound.title", "Chambre introuvable") : t("search.errorTitle", "Chargement impossible")}
          crumbs={[
            { label: t("menu.home"), to: langPath(lang, "/") },
            { label: listLabel, to: langPath(lang, "/chambre_de_commerces") },
          ]}
        />
        <section className="caci-section">
          <div className="caci-shell">
            <SectionNote
              state={missing ? "empty" : "error"}
              title={missing
                ? t("notfound.heading_chambre", "Cette chambre n'existe pas ou a été retirée.")
                : t("home.section_error", "Cette chambre n'a pas pu être chargée.")}
              action={
                missing ? (
                  <button type="button" className="btn-quiet" onClick={() => navigate(langPath(lang, "/chambre_de_commerces"))}>
                    <i className={`bi bi-arrow-${isRtl ? "right" : "left"}`} />
                    {listLabel}
                  </button>
                ) : (
                  <button type="button" className="btn-quiet" onClick={load}>
                    <i className="bi bi-arrow-clockwise" />
                    {t("home.retry", "Réessayer")}
                  </button>
                )
              }
            />
          </div>
        </section>
      </div>
    );
  }

  const loading = state === "loading";

  return (
    <div className={`caci-page${isRtl ? " is-rtl" : ""}`} ref={pageRef} dir={isRtl ? "rtl" : "ltr"}>
      <CaciStyles />
      <CaciDetailStyles />
      <style>{PAGE_CSS}</style>

      {state === "ready" && (
        <Helmet>
          <title>{name} — {t("menu.caci")}</title>
          <meta name="description" content={summary || name} />
          <link rel="canonical" href={typeof window !== "undefined" ? window.location.href : ""} />
          <meta property="og:title" content={name} />
          <meta property="og:description" content={summary || name} />
          {logo && <meta property="og:image" content={logo} />}
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content={t("menu.caci")} />
        </Helmet>
      )}

      <div
        className="caci-progress"
        style={{ transform: `scaleX(${progress})`, opacity: progress > 0.005 ? 1 : 0 }}
        aria-hidden="true"
      />

      <PageHeader
        title={state === "ready" ? name : listLabel}
        sub={wilaya}
        crumbs={[
          { label: t("menu.home"), to: langPath(lang, "/") },
          { label: listLabel, to: langPath(lang, "/chambre_de_commerces") },
          { label: state === "ready" ? truncate(name, 40) : listLabel },
        ]}
      />

      {/* ══════════════ PROFILE BAND ══════════════
          Full width. This is what replaces the 320px card that used to sit
          beside an empty column. */}
      <section className="caci-section cd-top">
        <div className="caci-shell">
          {loading ? (
            <div className="cd-card cd-profile" aria-hidden="true">
              <div className="cd-identity">
                <Skel w={104} h={104} r={18} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Skel h={13} r={7} w="45%" style={{ display: "block" }} />
                  <Skel h={22} r={9} w="72%" style={{ display: "block", marginBlockStart: 12 }} />
                </div>
              </div>
              <div className="cd-contact">
                {[0, 1, 2, 3].map((k) => (
                  <div className="cd-contact-row" key={k}>
                    <Skel w={38} h={38} r={11} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Skel h={9} r={5} w="36%" style={{ display: "block" }} />
                      <Skel h={13} r={7} w="76%" style={{ display: "block", marginBlockStart: 8 }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="cd-card cd-profile" data-rv="rise">
              <div className="cd-identity">
                <div className={`cd-logo${logo ? "" : " is-empty"}`}>
                  {logo ? (
                    <img
                      src={logo} alt="" loading="eager" decoding="async"
                      onError={(e) => {
                        const box = e.currentTarget.closest(".cd-logo");
                        e.currentTarget.style.display = "none";
                        if (box) box.classList.add("is-empty");
                      }}
                    />
                  ) : null}
                  <Seal size={44} className="cd-logo-ph" />
                </div>

                <div className="cd-identity-text">
                  {wilaya && (
                    <p className="cd-wilaya">
                      <Seal size={13} />
                      {wilaya}
                    </p>
                  )}
                  <h2 className="cd-name" dir="auto">{name}</h2>
                  {(admin.length > 0 || elected.length > 0) && (
                    <p className="cd-stat">
                      {elected.length > 0 && (
                        <span>
                          <b>{elected.length}</b> {t("home.elected", "Membres élus")}
                        </span>
                      )}
                      {admin.length > 0 && (
                        <span>
                          <b>{admin.length}</b> {t("home.administrative", "Personnel administratif")}
                        </span>
                      )}
                    </p>
                  )}
                </div>
              </div>

              {hasContact && (
                <div className="cd-contact">
                  {emails.map((e, i) => (
                    <ContactRow key={`m${i}`} icon="envelope" label={t("contact.email", "E-mail")}>
                      <a href={`mailto:${e}`} dir="ltr">{e}</a>
                    </ContactRow>
                  ))}
                  {phones.map((p, i) => (
                    <ContactRow key={`p${i}`} icon="telephone" label={t("contact.phone", "Téléphone")}>
                      <a href={`tel:${telHref(p)}`} dir="ltr">{formatPhone(p)}</a>
                    </ContactRow>
                  ))}
                  {faxes.map((f, i) => (
                    <ContactRow key={`f${i}`} icon="printer" label={t("contact.fax", "Fax")}>
                      <span dir="ltr">{formatPhone(f)}</span>
                    </ContactRow>
                  ))}
                  {address && (
                    <ContactRow icon="geo-alt" label={t("contact.address", "Adresse")} wide>
                      <span dir="auto">{address}</span>
                    </ContactRow>
                  )}
                </div>
              )}

              {(directions || emails.length > 0) && (
                <div className="cd-actions">
                  {directions && (
                    <a className="btn-solid" href={directions} target="_blank" rel="noopener noreferrer">
                      <i className="bi bi-signpost-split" />
                      {t("home.directions", "Itinéraire")}
                    </a>
                  )}
                  {emails.length > 0 && (
                    <a className="btn-quiet" href={`mailto:${emails[0]}`}>
                      <i className="bi bi-send" />
                      {t("contact.write", "Nous écrire")}
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ══════════════ PRESENTATION ══════════════ */}
      {state === "ready" && bodyHtml && (
        <section className="caci-section cd-band">
          <div className="caci-shell">
            <SectionHead title={t("home.chambres_title", "Présentation")} />
            <Prose html={bodyHtml} className="cd-prose" />
          </div>
        </section>
      )}

      {/* ══════════════ ROSTERS ══════════════ */}
      {state === "ready" && (admin.length > 0 || elected.length > 0) && (
        <section className="caci-section cd-teams">
          <div className="caci-shell">
            {admin.length > 0 && (
              <Roster title={t("home.administrative", "Personnel administratif")} members={admin} lang={lang} />
            )}
            {elected.length > 0 && (
              <Roster title={t("home.elected", "Membres élus")} members={elected} lang={lang} />
            )}
            
          </div>
        </section>
      )}

      {/* A chamber with no description, no roster and no coordinates used to
          leave the page stopping dead under the identity card. Say so calmly
          instead. */}
      {state === "ready" && !bodyHtml && !hasMap && admin.length === 0 && elected.length === 0 && (
        <section className="caci-section cd-band">
          <div className="caci-shell">
            <SectionNote
              title={t("home.chambre_empty", "Fiche en cours de constitution")}
              text={t(
                "home.chambre_empty_text",
                "Les informations détaillées de cette chambre seront publiées prochainement."
              )}
              action={
                <button
                  type="button" className="btn-quiet"
                  onClick={() => navigate(langPath(lang, "/chambre_de_commerces"))}
                >
                  <i className={`bi bi-arrow-${isRtl ? "right" : "left"}`} />
                  {listLabel}
                </button>
              }
            />
          </div>
        </section>
      )}

      {/* ══════════════ MAP ══════════════
          Restored from the original page. A chamber is a building people have
          to physically reach. */}
      {state === "ready" && hasMap && (
        <section className="caci-section cd-band">
          <div className="caci-shell">
            <SectionHead
              title={t("home.location_title", "Nous trouver")}
              action={
                directions ? (
                  <a className="btn-quiet" href={directions} target="_blank" rel="noopener noreferrer">
                    <i className="bi bi-box-arrow-up-right" />
                    {t("home.open_map", "Ouvrir la carte")}
                  </a>
                ) : null
              }
            />
            <div className="cd-map" data-rv="zoom">
              <iframe
                title={t("home.location_title", "Nous trouver")}
                loading="lazy"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.008}%2C${lat - 0.006}%2C${lng + 0.008}%2C${lat + 0.006}&layer=mapnik&marker=${lat}%2C${lng}`}
              />
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

/* NOTE: this is a template literal. Do not put a backtick anywhere inside it —
   it closes the string and everything after is parsed as JavaScript. */
const PAGE_CSS = `
.caci-page .cd-top { padding-block-start: clamp(30px, 4.5vw, 56px); padding-block-end: 0; }
/* Nothing after the band: it must not butt straight up against the footer. */
.caci-page section.cd-top:last-of-type { padding-block-end: var(--pad); }
.caci-page .cd-band { padding-block: clamp(40px, 6vw, 76px); }
.caci-page .cd-teams { padding-block: clamp(40px, 6vw, 76px) var(--pad); }

.caci-page .cd-card {
  border: 1px solid var(--rule); border-radius: 22px;
  background: var(--surface);
  box-shadow: 0 30px 70px -46px rgba(8,21,14,.42);
}

/* ── Profile band ──────────────────────────────────────────────────────────
   Was a 320px sidebar next to an empty column. Full width, and the contact
   details run across it instead of stacking down a narrow strip. */
.caci-page .cd-profile {
  position: relative; overflow: hidden;
  padding: clamp(20px, 3vw, 34px);
}
.caci-page .cd-profile::before {
  content: ''; position: absolute; inset-block-start: 0; inset-inline: 0;
  height: 3px; background: linear-gradient(90deg, var(--pine), var(--leaf-2), var(--brass-2));
}

.caci-page .cd-identity {
  display: flex; align-items: center; gap: clamp(16px, 2.4vw, 26px);
  flex-wrap: wrap;
}
.caci-page .cd-logo {
  position: relative; flex: 0 0 auto;
  width: clamp(88px, 11vw, 116px); aspect-ratio: 1;
  display: grid; place-items: center; padding: 12px;
  border-radius: 18px; border: 1px solid var(--rule);
  background: linear-gradient(158deg, var(--mint), var(--paper));
}
.caci-page .cd-logo img { max-width: 100%; max-height: 100%; object-fit: contain; }
.caci-page .cd-logo .cd-logo-ph { display: none; color: var(--rule-2); }
.caci-page .cd-logo.is-empty .cd-logo-ph { display: block; }

.caci-page .cd-identity-text { flex: 1 1 260px; min-width: 0; }
.caci-page .cd-wilaya {
  display: flex; align-items: center; gap: 9px; margin: 0 0 8px;
  font-family: var(--mono); font-size: 11px; font-weight: 600;
  letter-spacing: .12em; text-transform: uppercase; color: var(--brass);
}
.caci-page .cd-name {
  margin: 0; font-family: var(--display);
  font-size: clamp(21px, 2.9vw, 31px); font-weight: 700;
  letter-spacing: -.024em; line-height: 1.18; color: var(--ink);
  overflow-wrap: break-word;
}
.caci-page .cd-stat {
  display: flex; flex-wrap: wrap; gap: 8px 18px; margin: 12px 0 0;
  font-family: var(--mono); font-size: 11px; letter-spacing: .06em;
  text-transform: uppercase; color: var(--muted);
}
.caci-page .cd-stat b { color: var(--pine); font-size: 13px; }

/* ── Contact grid ─────────────────────────────────────────────────────────
   Two columns from 640px, so a chamber with four details fills the band
   instead of running down one edge of the page. */
.caci-page .cd-contact {
  display: grid; gap: 2px;
  margin-block-start: clamp(20px, 2.6vw, 28px);
  padding-block-start: clamp(18px, 2.4vw, 24px);
  border-block-start: 1px solid var(--rule);
}
@media (min-width: 640px) {
  .caci-page .cd-contact { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 4px 26px; }
  .caci-page .cd-contact-row.is-wide { grid-column: 1 / -1; }
}
.caci-page .cd-contact-row {
  display: flex; align-items: flex-start; gap: 13px;
  padding-block: 10px; min-width: 0;
}
.caci-page .cd-contact-icon {
  flex: 0 0 auto; width: 38px; height: 38px; border-radius: 11px;
  display: grid; place-items: center;
  background: var(--mint); color: var(--pine); font-size: 15px;
  transition: background var(--t1) var(--e), color var(--t1) var(--e);
}
.caci-page .cd-contact-row:hover .cd-contact-icon { background: var(--pine); color: #fff; }
.caci-page .cd-contact-body { min-width: 0; padding-block-start: 1px; }
.caci-page .cd-contact-label {
  display: block; margin-block-end: 2px;
  font-family: var(--mono); font-size: 9.5px; font-weight: 700;
  letter-spacing: .13em; text-transform: uppercase; color: var(--muted);
}
.caci-page .cd-contact-value {
  display: block; font-size: 14.5px; line-height: 1.5;
  color: var(--txt); overflow-wrap: anywhere;
}
.caci-page .cd-contact-value a {
  color: var(--txt); text-decoration: none;
  transition: color var(--t1) var(--e);
}
.caci-page .cd-contact-value a:hover { color: var(--pine); text-decoration: underline; }

.caci-page .cd-actions {
  display: flex; flex-wrap: wrap; gap: 10px;
  margin-block-start: clamp(18px, 2.4vw, 24px);
  padding-block-start: clamp(16px, 2vw, 22px);
  border-block-start: 1px solid var(--rule);
}

/* ── Presentation ─────────────────────────────────────────────────────────
   Given its own full-width section rather than being crushed into the
   remaining column beside the identity card. */
.caci-page .cd-prose { max-width: 74ch; }

/* ── Rosters ──────────────────────────────────────────────────────────────── */
.caci-page .cd-team + .cd-team { margin-block-start: clamp(44px, 6vw, 76px); }
.caci-page .member-grid {
  display: grid; gap: clamp(14px, 2.2vw, 22px);
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 216px), 1fr));
  align-items: start;
}
.caci-page .member {
  display: flex; flex-direction: column; height: 100%; overflow: hidden;
  border: 1px solid var(--rule); border-radius: 18px;
  background: var(--surface);
  transition: transform var(--t2) var(--e), border-color var(--t1) var(--e),
              box-shadow var(--t2) var(--e);
}
.caci-page .member:hover {
  transform: translateY(-6px); border-color: rgba(176,138,46,.42);
  box-shadow: 0 30px 56px -32px rgba(15,74,48,.45);
}
/* The most senior member of each roster — first after the descending sort.
   Every card used to look identical, so the president was indistinguishable
   from a committee member. */
.caci-page .member.is-lead {
  border-color: rgba(176,138,46,.5);
  box-shadow: 0 20px 46px -30px rgba(176,138,46,.6);
}
@media (min-width: 720px) {
  .caci-page .member.is-lead { grid-column: span 2; }
  .caci-page .member.is-lead .member-photo { aspect-ratio: 16 / 11; }
  .caci-page .member.is-lead .member-info h3 { font-size: 18px; }
}

/* 4:5 anchored near the top: a fixed square with object-fit:cover cropped
   the heads out of portrait photographs. */
.caci-page .member-photo {
  position: relative; aspect-ratio: 4 / 5; overflow: hidden;
  background: linear-gradient(160deg, var(--mint), var(--paper));
  display: grid; place-items: center;
}
.caci-page .member-photo img {
  position: absolute; inset: 0; z-index: 1;
  width: 100%; height: 100%; object-fit: cover; object-position: center 20%;
  transition: transform var(--t2) var(--e);
  object-fit: contain;
}
.caci-page .member:hover .member-photo img { transform: scale(1.04); }
.caci-page .member-photo::after {
  content: ''; position: absolute; inset: 0; z-index: 2; pointer-events: none;
  background: linear-gradient(to top, rgba(8,21,14,.14), transparent 42%);
}
.caci-page .member-fallback { color: var(--rule-2); font-size: 44px; }
.caci-page .member-photo.is-empty { background: linear-gradient(160deg, var(--mint), var(--paper)); }
.caci-page .member-pin {
  position: absolute; z-index: 3; inset-block-start: 10px; inset-inline-end: 10px;
  width: 30px; height: 30px; border-radius: 50%;
  display: grid; place-items: center;
  background: rgba(255,255,255,.94); color: var(--brass);
  box-shadow: 0 6px 16px -6px rgba(8,21,14,.5);
}

.caci-page .member-info { padding: 14px 16px 18px; text-align: center; }
.caci-page .member-info h3 {
  margin: 0; font-family: var(--display); font-size: 15.5px; font-weight: 700;
  letter-spacing: -.018em; line-height: 1.3; color: var(--ink);
  overflow-wrap: break-word;
}
/* One consistent label. The database mixes "DIRECTEUR GENERAL" with
   "1er Vice-président de la CACI"; rendering the role as a small uppercase
   line makes that inconsistency invisible instead of glaring. */
.caci-page .member-info p {
  margin: 7px 0 0; font-family: var(--mono);
  font-size: 10px; font-weight: 600; line-height: 1.5;
  letter-spacing: .08em; text-transform: uppercase; color: var(--muted);
}
.caci-page.is-rtl .member-info p { letter-spacing: normal; }

/* ── Map ──────────────────────────────────────────────────────────────────── */
.caci-page .cd-map {
  overflow: hidden; border-radius: 20px; border: 1px solid var(--rule);
  aspect-ratio: 21 / 9; min-height: 280px; background: var(--mint);
  box-shadow: 0 30px 70px -44px rgba(8,21,14,.42);
}
.caci-page .cd-map iframe { width: 100%; height: 100%; border: 0; display: block; }
@media (max-width: 640px) {
  .caci-page .cd-map { aspect-ratio: 4 / 3; }
}
`;

export default ChambreDetail;